import { ReactNode, useId, useState } from 'react'
import { SampleCancellationModal } from '../components/sample-cancellation/SampleCancellationModal'
import { Link, useLocation, useParams } from 'react-router'
import {
  ArrowSquareOutIcon,
  ArrowsLeftRightIcon,
  BookIcon,
  CalendarBlankIcon,
  BuildingIcon,
  ClipboardTextIcon,
  CopyIcon,
  DetailsCard,
  DropIcon,
  AnalysisTypeApprovalStatusTag,
  FilledOutByInspectorSection,
  getFirstRequisition,
  getResultStatus,
  getSampleBusinessDisplayName,
  getTaskTitle,
  geometryToLongitudeLatitude,
  HashIcon,
  hasSampleBeenCompromised,
  ArchiveIcon,
  FileTextIcon,
  FlagIcon,
  MapPinIcon,
  MapWithMarker,
  MarkdownCard,
  Mtds,
  UserIcon,
  ScreenHeader,
  ScreenLayout,
  ScreenHeaderProperty,
  PencilSimpleIcon,
  SampleResultSection,
  SampleResultTag,
  SampleStatusTag,
  SampleProgress,
  SubstanceTags,
  SealWarningIcon,
  SubSamples,
  SubSamplesSection,
  TopBar,
  AppLayout,
  useAuth,
  useGetOkProgramsQuery,
  useGetOwnerNameQuery,
  useGetPlanByIdQuery,
  useGetPurposesQuery,
  useGetSampleByIdQuery,
  useGetSubPurposesQuery,
  useGetTaskByIdQuery,
  useGetContractByIdQuery,
  useBulkUpdateSampleStatus,
  useErrorToast,
  useUpdateSampleMutation,
  TargetIcon,
  type UpdateSample,
  toLocaleDateString,
  VirusIcon,
  ProhibitIcon,
  WarningIcon
} from 'prove-common'
import { FinalizeSampleModal } from '../components/finalize-sample/FinalizeSampleModal'
import { SampleShippingAddress } from '../components/sample-shipping-address/SampleShippingAddress'
import {
  formatPosition,
  getSampleStatus,
  isSampleEditable,
  isSamplePartiallyEditable
} from '../utils/sampleUtils'
import { normalizeProcessingErrorText } from '../utils/processingErrorUtils'

const COMMENT_CHAR_LIMIT = 1000

type SampleScreenProps = {
  logo: ReactNode
  sideBar?: ReactNode
  taskLinkTo?: string
  sampleActionsBaseUrl?: string
  isProveta?: boolean
  useMobileBar?: boolean
}

type SampleDetailsProps = {
  detailsProperties: ScreenHeaderProperty[]
  formProperties: ScreenHeaderProperty[]
  positionProperties: ScreenHeaderProperty[]
  purposeProperties: ScreenHeaderProperty[]
  contextProperties: ScreenHeaderProperty[]
  labProperties: ScreenHeaderProperty[]
  sample: NonNullable<ReturnType<typeof useGetSampleByIdQuery>['data']>
  requisition: NonNullable<ReturnType<typeof getFirstRequisition>>
  oldSample?: ReturnType<typeof useGetSampleByIdQuery>['data']
  diffSample?: ReturnType<typeof useGetSampleByIdQuery>['data']
}

const SampleDetails = ({
  detailsProperties,
  formProperties,
  positionProperties,
  purposeProperties,
  contextProperties,
  labProperties,
  sample,
  requisition,
  oldSample
}: SampleDetailsProps) => {
  const lonLat = geometryToLongitudeLatitude(sample.geometry)

  return (
    <Mtds.Grid data-gap='4'>
      <DetailsCard
        title='Detaljer'
        icon={<ClipboardTextIcon aria-hidden />}
        properties={detailsProperties}
      />
      <DetailsCard
        title='Fra skjema'
        icon={<FileTextIcon aria-hidden />}
        properties={formProperties}
      >
        <FilledOutByInspectorSection
          schema={requisition.analysisType.payloadSchema?.schema || ''}
          currentPayload={requisition.payload}
          oldPayload={getFirstRequisition(oldSample)?.payload}
          compact
        />
      </DetailsCard>
      <DetailsCard
        title='Posisjon'
        icon={<MapPinIcon aria-hidden />}
        properties={positionProperties}
      >
        {sample.accuratePosition && lonLat && (
          <>
            <MapWithMarker
              latitude={lonLat.latitude}
              longitude={lonLat.longitude}
            />
            <Mtds.Alert data-color='info'>
              Denne prøven har nøyaktig posisjon
            </Mtds.Alert>
          </>
        )}
      </DetailsCard>
      <DetailsCard
        title='Hensikt'
        icon={<TargetIcon aria-hidden />}
        properties={purposeProperties}
      />
      <DetailsCard
        title='Oppgave og plan'
        icon={<FileTextIcon aria-hidden />}
        properties={contextProperties}
      />
      <DetailsCard
        title='Laboratorium'
        icon={<BuildingIcon aria-hidden />}
        properties={labProperties}
      >
        {sample.shippingAddress && (
          <div className='grid grid-cols-[minmax(0,7.5rem)_minmax(0,1fr)] gap-[var(--mtds-4)]'>
            <div className='col-start-2 min-w-0'>
              <SampleShippingAddress
                address={sample.shippingAddress}
                showName={false}
              />
            </div>
          </div>
        )}
      </DetailsCard>
    </Mtds.Grid>
  )
}

export function SampleScreen({
  logo,
  sideBar,
  taskLinkTo,
  sampleActionsBaseUrl,
  isProveta = false,
  useMobileBar
}: Readonly<SampleScreenProps>) {
  const location = useLocation()
  const { isAdmin, userId } = useAuth()
  const { sampleId } = useParams()
  const {
    data: sample,
    error: errorSample,
    isPending: isPendingSample
  } = useGetSampleByIdQuery(sampleId)
  const {
    data: task,
    error: taskError,
    isLoading: isLoadingTask
  } = useGetTaskByIdQuery(sample?.taskId)
  const requisition = getFirstRequisition(sample)
  const { data: plan, isLoading: isLoadingPlan } = useGetPlanByIdQuery(
    sample?.planId
  )
  const { data: okPrograms } = useGetOkProgramsQuery(
    sample?.okProgramId != null
  )
  const { data: purposes } = useGetPurposesQuery(plan?.purposeId != null)
  const { data: subPurposes } = useGetSubPurposesQuery(
    task?.subPurposeId != null
  )
  const contractId = task?.contractId ?? plan?.contractId
  const { data: contract, isLoading: isLoadingContract } =
    useGetContractByIdQuery(contractId)
  const { data: inspector, isLoading: isLoadingInspector } =
    useGetOwnerNameQuery(sample?.userId)
  const updateSampleStatus = useBulkUpdateSampleStatus()
  const updateSample = useUpdateSampleMutation()
  const showStatusError = useErrorToast('merke prøven som sendt')
  const showCommentError = useErrorToast('lagre kommentaren')
  const okProgram = okPrograms?.find((p) => p.id === sample?.okProgramId)
  const purpose = purposes?.find((p) => p.id === plan?.purposeId)
  const subPurpose = subPurposes?.find((p) => p.id === task?.subPurposeId)
  const subSamples: SubSamples | undefined = sample?.subSamples ?? []
  const resultStatus = getResultStatus(
    requisition?.results,
    hasSampleBeenCompromised(sample)
  )
  const popId = useId()
  const [isSampleIdCopied, setIsSampleIdCopied] = useState(false)
  const [isModalOpen, setModalOpen] = useState(false)
  const [isCancellationOpen, setCancellationOpen] = useState(false)
  const handleEditFollowUp = () => {
    setModalOpen(true)
  }
  const handleCopySampleId = async () => {
    if (sampleId == null) return
    await navigator.clipboard.writeText(sampleId)
    setIsSampleIdCopied(true)
  }
  const handleMarkSampleAsSent = async () => {
    if (sample == null) return

    try {
      const data = await updateSampleStatus.mutateAsync({
        sampleIds: [sample.id],
        newStatus: 'SampleSent'
      })

      const updatedStatus = data?.statuses?.find(
        (status) => status.sampleId === sample.id
      )

      if (!updatedStatus) {
        Mtds.toast.danger(`Kunne ikke oppdatere status for prøve #${sample.id}`)
        return
      }

      if (updatedStatus.status.startsWith('Failed')) {
        console.error(`Prøve #${sample.id} feilet med: ${updatedStatus.status}`)
        Mtds.toast.danger(`Kunne ikke merke prøve #${sample.id} som sendt.`)
        return
      }

      Mtds.toast.success(`Prøve #${sample.id} er merket som sendt`)
    } catch (e) {
      showStatusError(e)
    }
  }
  const handleSaveComment = async (comment: string) => {
    if (sample == null) return

    const updatedSample: UpdateSample = {
      ...sample,
      sampleTypeId: sample.sampleType.id,
      sampntTypeId: sample.sampntType.id,
      takenDate: sample.takenDate,
      comment
    }

    try {
      await updateSample.mutateAsync({
        sampleId: sample.id,
        etag: sample.etag,
        body: updatedSample
      })
      Mtds.toast.success('Kommentar lagret')
    } catch (e) {
      showCommentError(e)
    }
  }

  const isEditable = isSampleEditable(sample)
  const isPartiallyEditable = isSamplePartiallyEditable(sample)
  const isDismissDisabled =
    sample?.state === 'Completed' || sample?.state === 'Closed'

  const editMoveDisabledTooltip = isEditable
    ? undefined
    : 'Prøven kan ikke redigeres eller flyttes fordi den er under analyse eller analysen er ferdig'

  const breadcrumbItems = [
    {
      label: 'Prøver',
      to: '/samples'
    },
    {
      label: sampleId ?? '',
      to: `/samples/${sampleId}`,
      icon: <HashIcon />
    }
  ]

  const latestProcessingError = requisition?.latestProcessingError
  const normalizedProcessingError = normalizeProcessingErrorText(
    latestProcessingError,
    'nb'
  )
  const taskLink = taskLinkTo
    ? `${taskLinkTo}/tasks/${task?.id}`
    : `/tasks/${task?.id}`
  const sampleActionBasePath = sampleActionsBaseUrl
    ? `${sampleActionsBaseUrl.replace(/\/$/, '')}/samples/${sampleId}`
    : `/samples/${sampleId}`
  const showSampleWorkflowActions = isProveta || sampleActionsBaseUrl != null
  let taskContent: ReactNode = '-'
  if (isLoadingTask) {
    taskContent = <Mtds.Skeleton />
  } else if (task) {
    taskContent = <Link to={taskLink}>{getTaskTitle(task)}</Link>
  }
  let planContent: ReactNode = '-'
  if (isLoadingPlan) {
    planContent = <Mtds.Skeleton />
  } else if (plan) {
    planContent = isProveta ? (
      plan.description
    ) : (
      <Link to={`/plans/${plan.id}`}>{plan.description}</Link>
    )
  }
  const okProgramValue =
    okProgram == null ? (
      '-'
    ) : (
      <>
        <strong>{okProgram.number}</strong> {okProgram.name}
      </>
    )
  const purposeValue = purpose ? (
    <Mtds.Flex data-align='center' data-gap='2'>
      {purpose.name}
      {plan && !isProveta ? (
        <Link
          to={`/plans/${plan.id}`}
          aria-label={`Gå til plan ${plan.description ?? ''}`.trim()}
        >
          <Mtds.Tag data-color='neutral' data-icon='none'>
            Plan
          </Mtds.Tag>
        </Link>
      ) : (
        <Mtds.Tag data-color='neutral' data-icon='none'>
          Plan
        </Mtds.Tag>
      )}
    </Mtds.Flex>
  ) : (
    '-'
  )
  const subPurposeValue = subPurpose?.name ?? '-'
  const sampleStatus = sample ? getSampleStatus(sample) : undefined
  const canMarkSampleAsSent = isProveta && sample?.state === 'SampleCreated'
  const externalReferenceLabel =
    sample?.externalReferenceType === 'LOKALITET'
      ? 'Lokalitetsnr.'
      : 'Produsentnr.'
  const compromisedReason =
    sample?.subSamples?.length === 1
      ? sample.subSamples[0].compromised_reason
      : undefined
  const properties: ScreenHeaderProperty[] = [
    ...(sampleStatus
      ? [
          {
            label: 'Status',
            icon: <FlagIcon aria-hidden />,
            value: <SampleStatusTag status={sampleStatus} />
          }
        ]
      : []),
    {
      label: 'Resultat',
      icon: <SealWarningIcon aria-hidden />,
      value: resultStatus ? <SampleResultTag status={resultStatus} /> : '-'
    },
    ...(latestProcessingError
      ? [
          {
            label: 'Feil under behandling',
            icon: <WarningIcon aria-hidden />,
            value: (
              <Mtds.Tag
                data-color='warning'
                data-tooltip={normalizedProcessingError}
                className='min-w-0 max-w-full'
              >
                <span className='min-w-0 overflow-hidden text-ellipsis whitespace-nowrap'>
                  {normalizedProcessingError}
                </span>
              </Mtds.Tag>
            )
          }
        ]
      : []),
    {
      label: 'Produkt',
      icon: <DropIcon aria-hidden />,
      value: sample?.sampleType.product ?? '-'
    },
    {
      label: 'Prøve av',
      icon: <DropIcon aria-hidden />,
      value: sample?.sampleType.matrix ?? '-'
    },
    {
      label: 'Analyser for',
      icon: <VirusIcon aria-hidden />,
      value: <SubstanceTags analysisType={requisition?.analysisType} />
    },
    {
      label: 'Uttaksdato',
      icon: <CalendarBlankIcon aria-hidden />,
      value: toLocaleDateString(sample?.takenDate) ?? '-'
    },
    {
      label: 'Uttakssted',
      icon: <MapPinIcon aria-hidden />,
      value: sample?.sampntType?.nameNo ?? '-'
    }
  ]
  const detailsProperties: ScreenHeaderProperty[] = [
    {
      label: 'Registrert av',
      icon: <UserIcon aria-hidden />,
      value:
        sample?.userId == null ? (
          '-'
        ) : isLoadingInspector ? (
          <Mtds.Skeleton />
        ) : (
          (inspector?.name ?? '-')
        )
    },
    {
      label: 'Registrert dato',
      icon: <CalendarBlankIcon aria-hidden />,
      value: toLocaleDateString(requisition?.createdDate) ?? '-'
    },
    {
      label: 'Arkiv',
      icon: <ArchiveIcon aria-hidden />,
      value: sample?.archiveId ?? 'Venter på arkiv'
    },
    {
      label: 'Virksomhet',
      icon: <BuildingIcon aria-hidden />,
      value: sample ? getSampleBusinessDisplayName(sample) : '-'
    },
    {
      label: 'Kun personnavn',
      icon: <UserIcon aria-hidden />,
      value:
        sample?.nameRedacted == null ? '-' : sample.nameRedacted ? 'Ja' : 'Nei'
    },
    {
      label: externalReferenceLabel,
      icon: <HashIcon aria-hidden />,
      value: sample?.externalReferenceId ?? '-'
    },
    {
      label: 'Org.nr.',
      icon: <HashIcon aria-hidden />,
      value: sample?.orgnr?.toString() ?? '-'
    },
    ...(compromisedReason != null
      ? [
          {
            label: 'Grunn til kansellering',
            icon: <WarningIcon aria-hidden />,
            value: compromisedReason
          }
        ]
      : [])
  ]
  const formProperties: ScreenHeaderProperty[] = []
  const positionProperties: ScreenHeaderProperty[] = [
    {
      label: 'Koordinater',
      icon: <MapPinIcon aria-hidden />,
      value: formatPosition(sample?.geometry?.coordinates)
    }
  ]
  const purposeProperties: ScreenHeaderProperty[] = [
    {
      label: 'Hensikt',
      icon: <TargetIcon aria-hidden />,
      value: purposeValue
    },
    {
      label: 'OK-program',
      icon: <HashIcon aria-hidden />,
      value: okProgramValue
    },
    {
      label: 'Underhensikt',
      icon: <TargetIcon aria-hidden />,
      value: subPurposeValue
    }
  ]
  const contextProperties: ScreenHeaderProperty[] = [
    {
      label: 'Oppgave',
      icon: <FileTextIcon aria-hidden />,
      value: taskContent
    },
    { label: 'Plan', icon: <FlagIcon aria-hidden />, value: planContent }
  ]
  const labProperties: ScreenHeaderProperty[] = [
    {
      label: 'Godkjenning',
      icon: <SealWarningIcon aria-hidden />,
      value: task?.analysisTypeApprovalStatus ? (
        <AnalysisTypeApprovalStatusTag
          status={task.analysisTypeApprovalStatus}
        />
      ) : (
        '-'
      )
    },
    {
      label: 'Kontrakt',
      icon: <FileTextIcon aria-hidden />,
      value: isLoadingContract ? (
        <Mtds.Skeleton />
      ) : contract == null ? (
        '-'
      ) : isProveta ? (
        contract.title
      ) : (
        <Link to={`/contracts/${contract.id}`}>{contract.title}</Link>
      )
    },
    {
      label: 'Laboratorium',
      icon: <BuildingIcon aria-hidden />,
      value: requisition?.lab.name ?? '-'
    }
  ]
  return (
    <AppLayout
      isLoading={isPendingSample}
      error={errorSample || taskError}
      sidebar={sideBar}
      useMobileBar={useMobileBar}
      topBar={
        <TopBar
          logo={logo}
          breadcrumbItems={breadcrumbItems}
          showNotificationBell={!isProveta}
        />
      }
      isFullWidth
    >
      {sample != null && (
        <ScreenLayout
          details={
            requisition != null ? (
              <SampleDetails
                detailsProperties={detailsProperties}
                formProperties={formProperties}
                positionProperties={positionProperties}
                purposeProperties={purposeProperties}
                contextProperties={contextProperties}
                labProperties={labProperties}
                sample={sample}
                requisition={requisition}
                oldSample={location.state?.oldSample}
                diffSample={location.state?.diffSample}
              />
            ) : undefined
          }
          header={
            <ScreenHeader
              title={
                <Mtds.Flex data-align='center' data-gap='1'>
                  <HashIcon />
                  {sampleId}
                </Mtds.Flex>
              }
              titleActions={
                <Mtds.Button
                  key={isSampleIdCopied ? 'sample-id-copied' : 'sample-id'}
                  data-variant='tertiary'
                  className='max-[480px]:hidden'
                  type='button'
                  onClick={handleCopySampleId}
                  aria-label='Kopier posenummer'
                  data-tooltip={
                    isSampleIdCopied
                      ? 'Posenummer kopiert'
                      : 'Kopier posenummer'
                  }
                >
                  <CopyIcon />
                </Mtds.Button>
              }
              properties={properties}
              actions={
                (isAdmin === true || sample.userId === `user:${userId}`) && (
                  <>
                    <Mtds.Button
                      aria-label='Handlingsmeny'
                      popoverTarget={popId}
                    />
                    <Mtds.Popover as='menu' id={popId}>
                      <li>
                        <Mtds.Button
                          as={Link}
                          to={`/samples/${sampleId}/changelog`}
                        >
                          <BookIcon />
                          Endringslogg
                        </Mtds.Button>
                      </li>
                      {requisition?.followUp != null && (
                        <li>
                          <Mtds.Button
                            onClick={handleEditFollowUp}
                            popoverTargetAction='hide'
                          >
                            <PencilSimpleIcon />
                            Rediger oppfølging
                          </Mtds.Button>
                        </li>
                      )}
                      {sampleActionsBaseUrl != null && (
                        <li>
                          <Mtds.Button
                            as='a'
                            href={sampleActionBasePath}
                            target='_blank'
                            rel='noreferrer'
                          >
                            <ArrowSquareOutIcon />
                            Åpne i Prøveta
                          </Mtds.Button>
                        </li>
                      )}
                      {showSampleWorkflowActions && (
                        <>
                          <li>
                            {sampleActionsBaseUrl ? (
                              <Mtds.Button
                                as='a'
                                href={
                                  isEditable || isPartiallyEditable
                                    ? `${sampleActionBasePath}/edit`
                                    : '#'
                                }
                                target='_blank'
                                rel='noreferrer'
                                aria-disabled={
                                  !isEditable && !isPartiallyEditable
                                }
                                data-tooltip={editMoveDisabledTooltip}
                              >
                                <PencilSimpleIcon />
                                Rediger i Prøveta
                              </Mtds.Button>
                            ) : (
                              <Mtds.Button
                                as={Link}
                                to={
                                  isEditable || isPartiallyEditable
                                    ? `${sampleActionBasePath}/edit`
                                    : '#'
                                }
                                aria-disabled={
                                  !isEditable && !isPartiallyEditable
                                }
                                data-tooltip={editMoveDisabledTooltip}
                              >
                                <PencilSimpleIcon />
                                Rediger
                              </Mtds.Button>
                            )}
                          </li>
                          <li>
                            {sampleActionsBaseUrl ? (
                              <Mtds.Button
                                as='a'
                                href={
                                  isEditable
                                    ? `${sampleActionBasePath}/move`
                                    : '#'
                                }
                                target='_blank'
                                rel='noreferrer'
                                aria-disabled={!isEditable}
                                data-tooltip={editMoveDisabledTooltip}
                              >
                                <ArrowsLeftRightIcon />
                                Flytt i Prøveta
                              </Mtds.Button>
                            ) : (
                              <Mtds.Button
                                as={Link}
                                to={
                                  isEditable
                                    ? `${sampleActionBasePath}/move`
                                    : '#'
                                }
                                aria-disabled={!isEditable}
                                data-tooltip={editMoveDisabledTooltip}
                              >
                                <ArrowsLeftRightIcon />
                                Flytt
                              </Mtds.Button>
                            )}
                          </li>
                          <li>
                            <Mtds.Button
                              type='button'
                              data-color='danger'
                              disabled={isDismissDisabled || !requisition}
                              popoverTarget={popId}
                              popoverTargetAction='hide'
                              onClick={() => setCancellationOpen(true)}
                            >
                              <ProhibitIcon /> Kansellering
                            </Mtds.Button>
                          </li>
                        </>
                      )}
                    </Mtds.Popover>
                    {isCancellationOpen && requisition && (
                      <SampleCancellationModal
                        sampleId={sample.id}
                        requisition={requisition}
                        onClose={() => setCancellationOpen(false)}
                      />
                    )}
                    {isModalOpen && requisition != null && (
                      <FinalizeSampleModal
                        requisition={requisition}
                        isOpen={isModalOpen}
                        setOpen={setModalOpen}
                        isEdit={true}
                      />
                    )}
                  </>
                )
              }
            />
          }
        >
          {requisition == null && (
            <Mtds.Alert data-color={'danger'}>
              Det er ikke opprettet en rekvisisjon for denne prøven.
            </Mtds.Alert>
          )}
          {requisition != null && (
            <>
              {sample?.state === 'AnalysisStarted' && (
                <Mtds.Alert data-color='neutral'>
                  Analysen er i gang og prøven kan derfor ikke redigeres.
                  Resultatene kan vurderes når prøven er «Klar til behandling».
                </Mtds.Alert>
              )}
              {isProveta &&
                task?.locationRequired &&
                sample.geometry === null && (
                  <Mtds.Alert data-color='warning' as={Mtds.Prose}>
                    <Mtds.Heading>Posisjon mangler</Mtds.Heading>
                    <p>
                      Denne oppgaven krever posisjon. Rediger prøven for å legge
                      til posisjon.
                    </p>
                    <Mtds.Button
                      as={Link}
                      to={isEditable ? `/samples/${sampleId}/edit` : '#'}
                      data-variant='primary'
                      aria-disabled={!isEditable}
                      data-tooltip={editMoveDisabledTooltip}
                    >
                      Rediger prøve
                    </Mtds.Button>
                  </Mtds.Alert>
                )}
              <Mtds.Grid data-gap='8' className='min-w-0 mt-4'>
                <SampleProgress sample={sample} />
                {canMarkSampleAsSent && (
                  <Mtds.Alert data-color='info' as={Mtds.Prose}>
                    <Mtds.Heading>Klar til å sende</Mtds.Heading>
                    <p>
                      Merk de her samme dag du sender dem. Det er viktig at vi
                      har riktige opplysninger for hver prøve.
                    </p>
                    <Mtds.Button
                      type='button'
                      data-variant={'primary'}
                      onClick={handleMarkSampleAsSent}
                      aria-busy={updateSampleStatus.isPending}
                      disabled={updateSampleStatus.isPending}
                    >
                      Merk prøve som sendt
                    </Mtds.Button>
                  </Mtds.Alert>
                )}
                <SampleResultSection
                  sample={sample}
                  requisition={requisition}
                  handleEditFollowUp={handleEditFollowUp}
                />
                <SubSamplesSection
                  sampleId={sample.id}
                  subSamples={subSamples ?? []}
                  requisition={requisition}
                  sampleStatus={getSampleStatus(sample)}
                />
                <MarkdownCard
                  title='Kommentar'
                  value={sample.comment ?? ''}
                  emptyText='Ingen kommentar'
                  maxLength={COMMENT_CHAR_LIMIT}
                  editAriaLabel='Rediger kommentar'
                  placeholder='Skriv en kommentar...'
                  onSave={handleSaveComment}
                  isSaving={updateSample.isPending}
                  updated={!!location.state?.diffSample?.comment}
                />
              </Mtds.Grid>
            </>
          )}
        </ScreenLayout>
      )}
    </AppLayout>
  )
}
